function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { useStoreActions } from "../../../contexts";
import CustomTooltip from "../charts/CustomTooltip/CustomTooltip";
import CustomXAxisTick from "../charts/CustomXAxisTick/CustomXAxisTick";
import CustomBarShape from "../charts/CustomBarShape/CustomBarShape";
import { chartColors } from "../charts/chartColors";
import { commonXAxisProps, commonBarChartProps, commonCartesianGridProps, commonBarProps } from "../charts/commonChartProps";
import { FilterType, PaymentMethodFilterValue, defaultAgreementStatusFilter } from "../../AgreementsDashboard/agreementsDashboardFilters";
import { getSortUrlParamValue, sortUrlParam } from "../../../hooks/useSortFromUrl";
import { utils } from "../../../utils";
import get from 'lodash/get';
import { routes } from "../../../constants";
const statusToText = {
  sent: 'Sent',
  reviewed: 'Reviewed',
  paymentMethodConnected: 'Payment method connected',
  microDepositInitiated: 'Micro deposits initiated',
  microDepositIssues: 'Micro deposits issues',
  noPaymentMethod: 'Missing payment method'
};
const dataKeyToLabel = {
  'counts.proposals.ok': 'Proposals',
  'counts.proposals.warning': 'Proposals',
  'counts.agreements.ok': 'Agreements',
  'counts.agreements.warning': 'Agreements'
};
const hasRadius = data => {
  var _data$counts$agreemen, _data$counts$proposal;
  return ((_data$counts$agreemen = data.counts.agreements) === null || _data$counts$agreemen === void 0 ? void 0 : _data$counts$agreemen.warning) === 0 && ((_data$counts$proposal = data.counts.proposals) === null || _data$counts$proposal === void 0 ? void 0 : _data$counts$proposal.warning) === 0;
};
const tooltipItemsOrder = {
  agreements: 1,
  proposals: 2
};
const getTooltipItems = payload => {
  if (!payload) {
    return;
  }
  return payload.filter(p => p.dataKey !== 'sent') // {dataKey: AgreementPipelineSeries}) {
  .sort((a, b) => get(tooltipItemsOrder, a.dataKey) - get(tooltipItemsOrder, b.dataKey)).map(p => {
    const data = p.payload;
    const dataKey = p.dataKey || '';
    return {
      id: p.dataKey,
      label: dataKeyToLabel[p.dataKey],
      inBrackets: utils.formatNumberWithCommas(get(data, dataKey)),
      color: p.color,
      dataKey: p.dataKey
    };
  });
};
const addUrlParamsToPage = (page, paramsObj) => {
  const params = new URLSearchParams();
  Object.keys(paramsObj).forEach(key => {
    const value = paramsObj[key];
    if (value) {
      params.set(key, value);
    }
  });
  return "".concat(page, "?").concat(params);
};
function getLinkUrl(type) {
  return function (data) {
    const paymentMethodFilterKey = type === 'agreements' ? FilterType.PaymentMethod : 'filter';
    const relationshipStatus = data.type;
    const targetRoute = ['microDepositIssues', 'noPaymentMethod'].includes(data.type) ? routes.agreementsDashboard : type === 'proposals' ? routes.proposalsDashboard : routes.agreementsDashboard;
    if (relationshipStatus === 'sent') {
      return addUrlParamsToPage(targetRoute, {
        filter: 'sent',
        [sortUrlParam]: getSortUrlParamValue('sentDate', 'desc')
      });
    } else if (relationshipStatus === 'reviewed') {
      return addUrlParamsToPage(targetRoute, {
        filter: 'reviewed',
        [sortUrlParam]: getSortUrlParamValue('statusUpdate', 'desc')
      });
    } else if (relationshipStatus === 'paymentMethodConnected') {
      return addUrlParamsToPage(targetRoute, {
        filter: 'paymentMethod',
        [sortUrlParam]: getSortUrlParamValue('statusUpdate', 'desc')
      });
    } else if (relationshipStatus === 'microDepositInitiated') {
      return addUrlParamsToPage(targetRoute, {
        [paymentMethodFilterKey]: "".concat(type === 'agreements' ? PaymentMethodFilterValue.VerificationInitiated : 'microDepositStarted'),
        [sortUrlParam]: getSortUrlParamValue('statusUpdate', 'desc')
      });
    } else if (relationshipStatus === 'microDepositIssues') {
      return addUrlParamsToPage(targetRoute, {
        [FilterType.AgreementStatus]: defaultAgreementStatusFilter.join(','),
        [paymentMethodFilterKey]: "".concat(PaymentMethodFilterValue.VerificationFailed),
        [sortUrlParam]: getSortUrlParamValue('statusUpdate', 'desc')
      });
    } else if (relationshipStatus === 'noPaymentMethod') {
      return addUrlParamsToPage(targetRoute, {
        [FilterType.AgreementStatus]: defaultAgreementStatusFilter.join(','),
        [paymentMethodFilterKey]: "".concat(PaymentMethodFilterValue.NotSet),
        [sortUrlParam]: getSortUrlParamValue('statusUpdate', 'desc')
      });
    } else {
      // `microDepositIssues` - but there's no such possible case
      return '';
    }
  };
}
export default function AgreementPipelineChart(props) {
  const {
    chartData
  } = props;
  const {
    logBI
  } = useStoreActions();
  const onBarClick = data => {
    logBI(11550, {
      column: statusToText[data.type],
      series: data.type,
      value: data
    });
  };
  return /*#__PURE__*/React.createElement(ResponsiveContainer, {
    width: "100%",
    height: "100%"
  }, /*#__PURE__*/React.createElement(BarChart, _extends({}, commonBarChartProps, {
    data: chartData
  }), /*#__PURE__*/React.createElement(CartesianGrid, commonCartesianGridProps), /*#__PURE__*/React.createElement(XAxis, _extends({}, commonXAxisProps, {
    dataKey: "type",
    tick: /*#__PURE__*/React.createElement(CustomXAxisTick, {
      getText: props => statusToText[props.payload.value]
    }),
    height: 72
  })), /*#__PURE__*/React.createElement(YAxis, {
    hide: true
  }), /*#__PURE__*/React.createElement(Tooltip, {
    cursor: false,
    content: /*#__PURE__*/React.createElement(CustomTooltip, {
      testid: "agreement-pl-tooltip",
      getTitle: label => statusToText[label],
      getItems: getTooltipItems
    })
  }), /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    dataKey: "counts.proposals.ok",
    stackId: "a",
    fill: chartColors.lightGray,
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      getHasRadius: hasRadius,
      testid: "agreement-pl-widget-chart-count",
      getLinkUrl: getLinkUrl('proposals')
    }),
    onClick: onBarClick
  })), /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    dataKey: "counts.agreements.ok",
    stackId: "a",
    fill: chartColors.lightGray,
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      getHasRadius: hasRadius,
      testid: "agreement-pl-widget-chart-count",
      getLinkUrl: getLinkUrl('agreements')
    }),
    onClick: onBarClick
  })), /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    dataKey: "counts.proposals.warning",
    stackId: "a",
    fill: chartColors.red,
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      hasRadius: true,
      testid: "agreement-pl-widget-chart-error-count",
      getLinkUrl: getLinkUrl('proposals')
    }),
    onClick: onBarClick
  })), /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    dataKey: "counts.agreements.warning",
    stackId: "a",
    fill: chartColors.red,
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      hasRadius: true,
      testid: "agreement-pl-widget-chart-error-count",
      getLinkUrl: getLinkUrl('agreements')
    }),
    onClick: onBarClick
  }), /*#__PURE__*/React.createElement(LabelList, {
    position: "top",
    valueAccessor: entry => {
      var _entry$counts$agreeme, _entry$counts$agreeme2, _entry$counts$proposa, _entry$counts$proposa2;
      return (((_entry$counts$agreeme = entry.counts.agreements) === null || _entry$counts$agreeme === void 0 ? void 0 : _entry$counts$agreeme.ok) || 0) + (((_entry$counts$agreeme2 = entry.counts.agreements) === null || _entry$counts$agreeme2 === void 0 ? void 0 : _entry$counts$agreeme2.warning) || 0) + (((_entry$counts$proposa = entry.counts.proposals) === null || _entry$counts$proposa === void 0 ? void 0 : _entry$counts$proposa.ok) || 0) + (((_entry$counts$proposa2 = entry.counts.proposals) === null || _entry$counts$proposa2 === void 0 ? void 0 : _entry$counts$proposa2.warning) || 0);
    },
    fill: chartColors.labels,
    fontSize: 12
  }))));
}